import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../context/AuthContext';
import { supabase } from '@shared/lib/supabase/client';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const newPasswordSchema = z.object({
  password: z.string().min(6, 'Wachtwoord moet minimaal 6 tekens bevatten'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Wachtwoorden komen niet overeen',
  path: ['confirmPassword'],
});

type NewPasswordFormData = z.infer<typeof newPasswordSchema>;

export function NewPasswordForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updatePassword, error, clearError, session, loading: authLoading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  const tokenHash = searchParams.get('token_hash');
  const type = searchParams.get('type');
  const [otpVerifying, setOtpVerifying] = useState(!!tokenHash);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
  });

  // Verify token_hash via verifyOtp (prefetch-resistant flow)
  useEffect(() => {
    async function verifyTokenHash() {
      if (tokenHash && type === 'recovery') {
        await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        });
      }
      setOtpVerifying(false);
    }
    if (tokenHash) {
      verifyTokenHash();
    }
  }, [tokenHash, type]);

  // Wait for auth and OTP verification to finish before checking session
  useEffect(() => {
    if (!authLoading && !otpVerifying) {
      setSessionChecked(true);
    }
  }, [authLoading, otpVerifying]);

  const onSubmit = async (data: NewPasswordFormData) => {
    try {
      setIsSubmitting(true);
      clearError();
      await updatePassword(data.password);
      setUpdateSuccess(true);
    } catch (_err) {
      // Error is already set in context
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while auth is initializing
  if (!sessionChecked) {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Laden...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error if no valid session (expired or invalid token)
  if (!session) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Ongeldige of verlopen link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Deze wachtwoord-reset link is ongeldig of verlopen.
            Vraag een nieuwe reset link aan.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            className="w-full"
            onClick={() => navigate('/forgot-password')}
          >
            Nieuwe reset link aanvragen
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // Show success message after password update
  if (updateSuccess) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            Wachtwoord gewijzigd
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Uw wachtwoord is succesvol gewijzigd. U kunt nu inloggen met uw nieuwe wachtwoord.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            className="w-full"
            onClick={() => navigate('/login')}
          >
            Naar inloggen
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Nieuw wachtwoord instellen</CardTitle>
        <CardDescription>
          Voer uw nieuwe wachtwoord in
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">Nieuw wachtwoord</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              disabled={isSubmitting}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Wachtwoord bevestigen</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword')}
              disabled={isSubmitting}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Wachtwoord wijzigen...' : 'Wachtwoord wijzigen'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
